/**
 * Room Type Amenities Tab Component
 * Allows Company Admin to configure room types and their amenities for hotels
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { 
  Layers, Sparkles, Save, Plus, Settings, Users, Hotel,
  CheckCircle2, RefreshCw
} from 'lucide-react';

export default function RoomTypeAmenitiesTab({ companyId, hotels }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(hotels[0]?.id || '');
  const [roomTypes, setRoomTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [configs, setConfigs] = useState([]);
  
  // Current editing state
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [formData, setFormData] = useState({
    default_amenity_ids: [],
    default_adult_capacity: '',
    max_adult_capacity: '',
    default_child_capacity: '',
    max_child_capacity: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedHotel) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [roomTypesRes, amenitiesRes, configsRes] = await Promise.all([
        api.get('/room-types'),
        api.get('/amenities'),
        api.get(`/companies/hotels/${selectedHotel}/room-type-config`).catch(() => ({ data: [] }))
      ]);
      
      setRoomTypes(roomTypesRes.data.filter(rt => rt.is_active));
      setAmenities(amenitiesRes.data.filter(a => a.is_active));
      setConfigs(configsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedHotel]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    // When hotel changes, reset selection
    setSelectedRoomType('');
    setFormData({
      default_amenity_ids: [],
      default_adult_capacity: '',
      max_adult_capacity: '',
      default_child_capacity: '',
      max_child_capacity: ''
    });
  }, [selectedHotel]);

  const handleRoomTypeSelect = (roomTypeId) => {
    setSelectedRoomType(roomTypeId);
    
    // Load existing config for this room type
    const existingConfig = configs.find(c => c.room_type_id === roomTypeId);
    if (existingConfig) {
      setFormData({
        default_amenity_ids: existingConfig.default_amenity_ids || [],
        default_adult_capacity: existingConfig.default_adult_capacity?.toString() || '',
        max_adult_capacity: existingConfig.max_adult_capacity?.toString() || '',
        default_child_capacity: existingConfig.default_child_capacity?.toString() || '',
        max_child_capacity: existingConfig.max_child_capacity?.toString() || ''
      });
    } else {
      setFormData({
        default_amenity_ids: [],
        default_adult_capacity: '',
        max_adult_capacity: '',
        default_child_capacity: '',
        max_child_capacity: ''
      });
    }
  };

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      default_amenity_ids: prev.default_amenity_ids.includes(amenityId)
        ? prev.default_amenity_ids.filter(id => id !== amenityId)
        : [...prev.default_amenity_ids, amenityId]
    }));
  };

  const handleSave = async () => {
    if (!selectedHotel || !selectedRoomType) {
      toast.error('Please select a hotel and room type');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/companies/hotels/${selectedHotel}/room-type-config`, {
        room_type_id: selectedRoomType,
        default_amenity_ids: formData.default_amenity_ids,
        default_adult_capacity: formData.default_adult_capacity ? parseInt(formData.default_adult_capacity) : null,
        max_adult_capacity: formData.max_adult_capacity ? parseInt(formData.max_adult_capacity) : null,
        default_child_capacity: formData.default_child_capacity ? parseInt(formData.default_child_capacity) : null,
        max_child_capacity: formData.max_child_capacity ? parseInt(formData.max_child_capacity) : null
      });
      toast.success('Configuration saved successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const getConfigForRoomType = (roomTypeId) => {
    return configs.find(c => c.room_type_id === roomTypeId);
  };

  const getRoomTypeName = (id) => {
    return roomTypes.find(rt => rt.id === id)?.name || 'Unknown';
  };

  if (hotels.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Hotel className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <p>No hotels found for this company</p>
        <p className="text-sm">Add a hotel first to configure room types</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="room-type-amenities-tab">
      {/* Hotel Selector */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Label className="text-sm mb-2 block">Select Hotel</Label>
          <Select value={selectedHotel} onValueChange={setSelectedHotel}>
            <SelectTrigger>
              <SelectValue placeholder="Select a hotel" />
            </SelectTrigger>
            <SelectContent>
              {hotels.map(hotel => (
                <SelectItem key={hotel.id} value={hotel.id}>
                  <div className="flex items-center gap-2">
                    <Hotel className="h-4 w-4" />
                    {hotel.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="icon" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {selectedHotel && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Room Types List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Layers className="h-4 w-4 text-blue-500" />
                Room Types
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {roomTypes.map(roomType => {
                    const config = getConfigForRoomType(roomType.id);
                    const isSelected = selectedRoomType === roomType.id;
                    const hasConfig = !!config;
                    
                    return (
                      <div
                        key={roomType.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected 
                            ? 'border-blue-500 bg-blue-50' 
                            : hasConfig 
                              ? 'border-green-200 bg-green-50 hover:bg-green-100' 
                              : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => handleRoomTypeSelect(roomType.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Layers className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{roomType.name}</span>
                          </div>
                          {hasConfig && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        {hasConfig && (
                          <div className="mt-2 text-xs text-gray-500">
                            <span>{config.default_amenity_ids?.length || 0} amenities</span>
                            <span className="mx-2">•</span>
                            <span>Adults: {config.default_adult_capacity || '-'}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Configuration Form */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Settings className="h-4 w-4 text-orange-500" />
                Configure {selectedRoomType ? getRoomTypeName(selectedRoomType) : 'Room Type'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedRoomType ? (
                <div className="text-center py-8 text-gray-500">
                  <Layers className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">Select a room type to configure</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Guest Capacity */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      Guest Capacity
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Default Adults</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.default_adult_capacity}
                          onChange={(e) => setFormData({...formData, default_adult_capacity: e.target.value})}
                          placeholder="2"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Max Adults</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.max_adult_capacity}
                          onChange={(e) => setFormData({...formData, max_adult_capacity: e.target.value})}
                          placeholder="4"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Default Children</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.default_child_capacity}
                          onChange={(e) => setFormData({...formData, default_child_capacity: e.target.value})}
                          placeholder="0"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Max Children</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.max_child_capacity}
                          onChange={(e) => setFormData({...formData, max_child_capacity: e.target.value})}
                          placeholder="2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Default Amenities */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-orange-500" />
                      Default Amenities
                    </Label>
                    <ScrollArea className="h-[150px] border rounded-lg p-3">
                      <div className="space-y-2">
                        {amenities.map(amenity => {
                          const isChecked = formData.default_amenity_ids.includes(amenity.id);
                          return (
                            <label
                              key={amenity.id}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${
                                isChecked ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50'
                              }`}
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => toggleAmenity(amenity.id)}
                              />
                              <Sparkles className="h-3 w-3 text-orange-400" />
                              <span className="text-sm">{amenity.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </ScrollArea>
                    <p className="text-xs text-gray-500">
                      Selected: {formData.default_amenity_ids.length} amenities
                    </p>
                  </div>

                  {/* Save Button */}
                  <Button 
                    onClick={handleSave} 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
